/**
 * Centralised `queryOptions()` factories.
 *
 * Both the page's `useQuery` and the TabBar prefetch consume the same
 * factory so a prefetch on pointer-down lands in the exact cache slot
 * the destination page reads. `placeholderData: keepPreviousData` keeps
 * the last render visible while a refetch runs, eliminating the
 * "Loading…" flash on tab-switches.
 */
import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import {
  api,
  type MemoryVerseThisWeek,
  type Prayer,
  type SermonNotesResponse,
  type TodayResponse,
} from './api';
import type { Circle } from './circles';
import type { VideoRow } from './video-tags';

const ONE_MIN = 60_000;
const FIVE_MIN = 5 * 60_000;

// Photo response shapes used by the /memories prefetch + page. Kept in
// sync with the local interfaces in routes/memories.tsx — the page
// imports these to avoid drift.
export interface PhotoListItem {
  id: string;
  url: string;
  takenAt: number | null;
  caption?: string | null;
}
export interface PhotoGridResponse {
  photos: PhotoListItem[];
  nextCursor?: number | null;
}
export interface PhotoYearsResponse {
  years: { year: number; count: number }[];
}
export interface PhotoSuggestedResponse {
  groups: { label: string; photos: PhotoListItem[] }[];
}

export function todayQueryOptions() {
  return queryOptions({
    queryKey: ['today'] as const,
    queryFn: () => api.get<TodayResponse>('/today'),
    staleTime: FIVE_MIN,
    placeholderData: keepPreviousData,
  });
}

export function photoYearsQueryOptions() {
  return queryOptions({
    queryKey: ['photo-years'] as const,
    queryFn: () => api.get<PhotoYearsResponse>('/photos/years'),
    staleTime: ONE_MIN,
    placeholderData: keepPreviousData,
  });
}

export function photoSuggestedQueryOptions() {
  return queryOptions({
    queryKey: ['photo-suggested'] as const,
    queryFn: () => api.get<PhotoSuggestedResponse>('/photos/suggested'),
    staleTime: ONE_MIN,
    placeholderData: keepPreviousData,
  });
}

export function photoGridQueryOptions(year: number | null) {
  return queryOptions({
    queryKey: ['photo-grid', year] as const,
    queryFn: () =>
      api.get<PhotoGridResponse>(year ? `/photos/grid?year=${year}` : '/photos/grid'),
    staleTime: ONE_MIN,
    placeholderData: keepPreviousData,
  });
}

export function myCirclesQueryOptions() {
  return queryOptions({
    queryKey: ['circles'] as const,
    queryFn: () => api.get<{ circles: Circle[] }>('/circles'),
    staleTime: ONE_MIN,
    placeholderData: keepPreviousData,
  });
}

export function memoryVerseThisWeekQueryOptions() {
  return queryOptions({
    queryKey: ['memory-verse', 'this-week'] as const,
    queryFn: () => api.get<MemoryVerseThisWeek>('/memory-verse/this-week'),
    staleTime: ONE_MIN,
    placeholderData: keepPreviousData,
  });
}

export function videosQueryOptions() {
  return queryOptions({
    queryKey: ['videos'] as const,
    queryFn: () => api.get<{ videos: VideoRow[] }>('/videos'),
    staleTime: FIVE_MIN,
    placeholderData: keepPreviousData,
  });
}

export function prayersQueryOptions(tab: 'active' | 'answered') {
  return queryOptions({
    queryKey: ['prayers', tab] as const,
    queryFn: () => api.get<{ prayers: Prayer[] }>(`/prayers?status=${tab}`),
    staleTime: ONE_MIN,
    placeholderData: keepPreviousData,
  });
}

export function sermonNotesQueryOptions() {
  return queryOptions({
    queryKey: ['sermon-notes'] as const,
    queryFn: () => api.get<SermonNotesResponse>('/sermon-notes'),
    staleTime: ONE_MIN,
    placeholderData: keepPreviousData,
  });
}

// ------------------------------------------------------------ reading plan
export interface ReadingPlanPassage {
  passageIndex: number;
  passageRef: string;
  youversionUrl: string;
  completed: boolean;
}
export interface ReadingPlanWeekDay {
  dayNum: number;
  fullyDone: boolean;
  isCurrent: boolean;
  passages: ReadingPlanPassage[];
}
export interface ReadingPlanMeResponse {
  subscription: {
    planSlug: string;
    planName: string;
    totalDays: number;
    startDate: string;
  } | null;
  week: {
    currentDay: number;
    days: ReadingPlanWeekDay[];
  } | null;
  behindBy: number;
  streak: number;
  endReached: boolean;
  stats: {
    daysCompleted: number;
    daysCompletedOnTime: number;
  };
  family: {
    memberId: string;
    displayName: string;
    currentDay: number;
    completedToday: boolean;
  }[];
}

export interface ReadingPlanCatalogEntry {
  slug: string;
  name: string;
  totalDays: number;
}

export function readingPlansListQueryOptions() {
  return queryOptions({
    queryKey: ['reading-plans', 'list'] as const,
    queryFn: () => api.get<{ plans: ReadingPlanCatalogEntry[] }>('/reading-plans'),
    staleTime: 5 * 60_000, // plan catalog rarely changes
    placeholderData: keepPreviousData,
  });
}

export function readingPlanMeQueryOptions() {
  return queryOptions({
    queryKey: ['reading-plan', 'me'] as const,
    queryFn: () => api.get<ReadingPlanMeResponse>('/reading-plans/me'),
    staleTime: ONE_MIN,
    placeholderData: keepPreviousData,
  });
}

// ------------------------------------------------------------ memories
export interface MemoryListItem {
  id: string;
  title: string;
  description: string | null;
  eventDate: string | null;
  coverPhotoId: string | null;
  photoCount: number;
  sharedBy: string | null;
  sharedAt: number | null;
  createdAt: number;
}

export interface MemoryDetail {
  id: string;
  title: string;
  description: string | null;
  eventDate: string | null;
  photoIds: string[];
  coverPhotoId: string | null;
  sharedBy: string | null;
  sharedAt: number | null;
  createdAt: number;
}

export function memoriesListQueryOptions() {
  return queryOptions({
    queryKey: ['memories', 'list'] as const,
    queryFn: () => api.get<{ memories: MemoryListItem[] }>('/memories'),
    staleTime: ONE_MIN,
    placeholderData: keepPreviousData,
  });
}

export function memoryDetailQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['memories', 'detail', id] as const,
    queryFn: () => api.get<MemoryDetail>(`/memories/${id}`),
    staleTime: ONE_MIN,
    placeholderData: keepPreviousData,
  });
}

// ------------------------------------------------------------- recap
export interface RecapResponse {
  year: number;
  photoCount: number;
  prayerCount: number;
  prayersAnswered: number;
  journalEntries: number;
  gratitudeEntries: number;
  memoryVerseWeeksCompleted: number;
  readingPlanDaysCompleted: number;
  topMonthByPhotos: { month: number; count: number } | null;
  coverPhotoId: string | null;
}

export function recapYearsQueryOptions() {
  return queryOptions({
    queryKey: ['recap', 'years'] as const,
    queryFn: () => api.get<{ years: number[] }>('/recap'),
    staleTime: FIVE_MIN,
    placeholderData: keepPreviousData,
  });
}

export function recapYearQueryOptions(year: number) {
  return queryOptions({
    queryKey: ['recap', 'year', year] as const,
    queryFn: () => api.get<RecapResponse>(`/recap/${year}`),
    staleTime: FIVE_MIN,
    placeholderData: keepPreviousData,
  });
}
